// Ported from RaceCut (lib/racecut/caption-draw.ts). Keep the two in step when fixing
// a drawing or encoding bug; the export and the preview both depend on it.
/**
 * THE ONE CAPTION DRAWER - the studio canvas and the export both call this, so what the
 * person sees is what the video gets, by construction. Everything it reads is data: the
 * words with their times, the style the panel edits, and the preset from the catalogue.
 */
import type { CaptionWord } from "./layers";
import {
  presetNamed,
  type CaptionPreset,
  type Entrance,
  type Key,
} from "./captionPresets";
import {
  captionFontCss,
  captionFontPx,
  captionText,
  phrasesOf,
  type CaptionStyle,
} from "./layers";

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/** Where the block landed, in pixels of the frame it was drawn on. */
export type DrawnBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** A word stays up until the next one starts, capped at its end plus a short hold. */
const HOLD_SECS = 0.6;
const SHORTEST_SHOW_SECS = 0.2;
/** Of the frame's width, so a phrase never touches the edges. */
const WIDEST_LINE = 0.86;
const LINE_HEIGHT = 1.18;
const SPACE = 0.28;
/** A word is typed out over its own timestamps: nothing before it starts, all of it once it has been sung. */
function typedSoFar(text: string, word: CaptionWord, t: number): string {
  if (t >= word.end) return text;
  if (t < word.start) return "";
  return text.slice(
    0,
    Math.ceil(
      (text.length * (t - word.start)) / Math.max(word.end - word.start, 0.001),
    ),
  );
}

type Shown = {
  words: CaptionWord[];
  first: number;
  start: number;
  since: number;
  /** How long the unit stays on screen. */ lasts: number;
};

function showsUntil(words: CaptionWord[], i: number, end: number): number {
  const nextStart = i + 1 < words.length ? words[i + 1].start : end + HOLD_SECS;
  return Math.max(
    words[i].start + SHORTEST_SHOW_SECS,
    Math.min(nextStart, end + HOLD_SECS),
  );
}

/** The unit on screen at `t`: one word, or one phrase, with how long it has been there. */
function shownAt(
  words: CaptionWord[],
  t: number,
  preset: CaptionPreset,
): Shown | null {
  if (preset.unit === "word") {
    const i = words.findIndex(
      (word, k) => t >= word.start && t < showsUntil(words, k, word.end),
    );
    return i < 0
      ? null
      : {
          words: [words[i]],
          first: i,
          start: words[i].start,
          since: t - words[i].start,
          lasts: words[i].end - words[i].start,
        };
  }
  const phrases = phrasesOf(words);
  const blocks = phrases.map((q) => ({
    text: q.text,
    start: q.start,
    end: q.end,
  }));
  const p = phrases.findIndex(
    (phrase, k) => t >= phrase.start && t < showsUntil(blocks, k, phrase.end),
  );
  if (p < 0) return null;
  const phrase = phrases[p];
  return {
    words: words.slice(phrase.first, phrase.last + 1),
    first: phrase.first,
    start: phrase.start,
    since: t - phrase.start,
    lasts: phrase.end - phrase.start,
  };
}

type Pose = {
  scale: number;
  opacity: number;
  tracking: number;
  blur: number;
  rise: number;
};

const AT_REST: Pose = { scale: 1, opacity: 1, tracking: 0, blur: 0, rise: 0 };

function keyed(
  keys: Key[],
  field: keyof Omit<Key, "at">,
  progress: number,
): number | undefined {
  const known = keys.filter((key) => key[field] !== undefined);
  if (!known.length) return undefined;
  const next =
    known.find((key) => key.at >= progress) ?? known[known.length - 1];
  const prev =
    [...known].reverse().find((key) => key.at <= progress) ?? known[0];
  if (next.at === prev.at) return next[field];
  const along = (progress - prev.at) / (next.at - prev.at);
  return prev[field]! + (next[field]! - prev[field]!) * along;
}

function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function poseAt(entrance: Entrance, since: number, lasts: number): Pose {
  if (entrance.kind !== "keys") return AT_REST;
  const secs =
    entrance.secs === "block" ? Math.max(lasts, 0.001) : entrance.secs;
  if (secs <= 0) return AT_REST;
  const raw = Math.max(0, Math.min(1, since / secs));
  const progress = entrance.secs === "block" ? raw : easeOut(raw);
  return {
    scale: keyed(entrance.keys, "scale", progress) ?? 1,
    opacity: keyed(entrance.keys, "opacity", progress) ?? 1,
    tracking: keyed(entrance.keys, "tracking", progress) ?? 0,
    blur: keyed(entrance.keys, "blur", progress) ?? 0,
    rise: keyed(entrance.keys, "rise", progress) ?? 0,
  };
}

type Placed = {
  text: string;
  word: CaptionWord;
  x: number;
  line: number;
  width: number;
};

/** A context that knows letter spacing measures it itself; one that doesn't gets it added by hand. */
function setTracking(ctx: Ctx, px: number) {
  if ("letterSpacing" in ctx)
    (ctx as CanvasRenderingContext2D).letterSpacing = `${px}px`;
}

function measure(ctx: Ctx, text: string, trackingPx: number): number {
  const byHand =
    "letterSpacing" in ctx ? 0 : trackingPx * Math.max(0, text.length - 1);
  return ctx.measureText(text).width + byHand;
}

/** Greedy lines, left-to-right, never wider than the frame allows. */
function layOut(
  ctx: Ctx,
  words: string[],
  shownWords: CaptionWord[],
  size: number,
  trackingPx: number,
  widest: number,
): { placed: Placed[]; lineWidths: number[] } {
  const space = size * SPACE + trackingPx;
  const placed: Placed[] = [];
  const lineWidths: number[] = [0];
  let line = 0;
  words.forEach((text, i) => {
    const width = measure(ctx, text, trackingPx);
    const wouldBe = lineWidths[line] + (lineWidths[line] ? space : 0) + width;
    if (lineWidths[line] && wouldBe > widest) {
      line += 1;
      lineWidths[line] = 0;
    }
    const x = lineWidths[line] + (lineWidths[line] ? space : 0);
    placed.push({ text, word: shownWords[i], x, line, width });
    lineWidths[line] = x + width;
  });
  return { placed, lineWidths };
}

function fillAndStroke(
  ctx: Ctx,
  text: string,
  x: number,
  y: number,
  size: number,
  stroke: CaptionPreset["stroke"],
  fill: string,
  trackingPx: number,
) {
  const spaced = "letterSpacing" in ctx;
  const glyphs = spaced ? [text] : text.split("");
  const advance = (glyph: string) =>
    ctx.measureText(glyph).width + (spaced ? 0 : trackingPx);
  let at = x;
  for (const glyph of glyphs) {
    if (stroke) {
      ctx.lineWidth = stroke.width * size * 2;
      ctx.strokeStyle = "#0A0A0A";
      ctx.strokeText(glyph, at, y);
    }
    ctx.fillStyle = fill;
    ctx.fillText(glyph, at, y);
    at += advance(glyph);
  }
}

/** Every frame is measured and laid out in this one, then scaled: a line breaks in the same place on a 282 px canvas and a 1080 px video. */
const REFERENCE = { width: 1080, height: 1920 };

/**
 * Draws whatever is on screen at `t` and says where it landed; nothing on screen returns null.
 * `W`/`H` are the frame's pixels; the style's x/y are fractions of them.
 */
export function drawCaptions(
  ctx: Ctx,
  frameWidth: number,
  frameHeight: number,
  words: CaptionWord[],
  t: number,
  style: CaptionStyle,
): DrawnBounds | null {
  const k = frameHeight / REFERENCE.height;
  ctx.save();
  ctx.scale(k, k);
  const bounds = drawInReference(
    ctx,
    (REFERENCE.width * (frameWidth / frameHeight)) /
      (REFERENCE.width / REFERENCE.height),
    REFERENCE.height,
    words,
    t,
    style,
  );
  ctx.restore();
  return (
    bounds && {
      x: bounds.x * k,
      y: bounds.y * k,
      width: bounds.width * k,
      height: bounds.height * k,
    }
  );
}

function drawInReference(
  ctx: Ctx,
  W: number,
  H: number,
  words: CaptionWord[],
  t: number,
  style: CaptionStyle,
): DrawnBounds | null {
  const preset = presetNamed(style.preset);
  const shown = shownAt(words, t, preset);
  if (!shown) return null;
  const size = captionFontPx(H, style);
  const fill = style.color;
  const pose = poseAt(preset.entrance, shown.since, shown.lasts);
  const trackingPx = pose.tracking * size;
  const texts = shown.words.map((word) =>
    captionText(word.text, style.textCase),
  );

  ctx.save();
  ctx.font = captionFontCss(size, style);
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.lineJoin = "round";
  // Lines are decided at the entrance's widest tracking, so a phrase that expands keeps its lines and only its letters drift apart.
  const widestTracking =
    preset.entrance.kind === "keys"
      ? Math.max(0, ...preset.entrance.keys.map((key) => key.tracking ?? 0))
      : 0;
  const layoutPx = Math.max(trackingPx, widestTracking * size);
  setTracking(ctx, layoutPx);
  const { placed, lineWidths } = layOut(
    ctx,
    texts,
    shown.words,
    size,
    layoutPx,
    W * WIDEST_LINE,
  );
  setTracking(ctx, trackingPx);
  const space = size * SPACE + trackingPx;
  lineWidths.fill(0);
  placed.forEach((item) => {
    item.width = measure(ctx, item.text, trackingPx);
    item.x = lineWidths[item.line] + (lineWidths[item.line] ? space : 0);
    lineWidths[item.line] = item.x + item.width;
  });
  const lines = lineWidths.length;
  const blockWidth = Math.max(...lineWidths);
  const lineStep = size * LINE_HEIGHT;
  const blockHeight = lineStep * lines;
  const anchorX = style.x * W;
  const foot = style.y * H;
  const left =
    style.align === "left"
      ? anchorX
      : style.align === "right"
        ? anchorX - blockWidth
        : anchorX - blockWidth / 2;
  const top = foot - blockHeight;
  const centreX = left + blockWidth / 2;
  const centreY = top + blockHeight / 2;

  const bounds = {
    x: left - size * 0.25,
    y: top - size * 0.25 + pose.rise * size,
    width: blockWidth + size * 0.5,
    height: blockHeight + size * 0.5,
  };
  if (pose.opacity <= 0) {
    ctx.restore();
    return bounds;
  }
  ctx.globalAlpha = pose.opacity;
  ctx.translate(centreX, centreY + pose.rise * size);
  ctx.scale(pose.scale, pose.scale);
  ctx.translate(-centreX, -centreY);
  if (pose.blur > 0 && "filter" in ctx)
    (ctx as CanvasRenderingContext2D).filter = `blur(${pose.blur * size}px)`;

  const lineLeft = (line: number) =>
    style.align === "left"
      ? left
      : style.align === "right"
        ? left + blockWidth - lineWidths[line]
        : left + (blockWidth - lineWidths[line]) / 2;
  const baselineOf = (line: number) =>
    top + lineStep * (line + 1) - size * 0.22;

  if (preset.pill) {
    ctx.fillStyle = style.pill;
    const pad = preset.pill.pad * size;
    for (let line = 0; line < lines; line++) {
      ctx.beginPath();
      ctx.roundRect(
        lineLeft(line) - pad,
        baselineOf(line) - size * 0.95 - pad * 0.4,
        lineWidths[line] + pad * 2,
        size * 1.2 + pad * 0.8,
        preset.pill.radius * size,
      );
      ctx.fill();
    }
  }
  if (preset.shadow) {
    ctx.shadowColor = "rgba(0,0,0,0.5)";
    ctx.shadowOffsetX = preset.shadow.x * size;
    ctx.shadowOffsetY = preset.shadow.y * size;
  }
  if (preset.glow) {
    ctx.shadowColor = style.glow;
    ctx.shadowBlur = preset.glow.blur * size;
  }

  placed.forEach((item, i) => {
    const sung = t >= item.word.start && t < item.word.end;
    const emphasised = preset.unit === "phrase" && preset.emphasis && sung;
    const text =
      preset.entrance.kind === "typed"
        ? typedSoFar(item.text, item.word, t)
        : item.text;
    if (!text) return;
    const x = lineLeft(item.line) + item.x;
    const y = baselineOf(item.line);
    const staggered =
      preset.entrance.kind === "keys" && preset.entrance.stagger
        ? poseAt(
            preset.entrance,
            shown.since - i * preset.entrance.stagger,
            shown.lasts,
          )
        : null;
    ctx.save();
    if (staggered) {
      ctx.globalAlpha = pose.opacity * staggered.opacity;
      ctx.translate(0, staggered.rise * size);
    }
    if (emphasised && preset.emphasis?.scale) {
      const cx = x + item.width / 2,
        cy = y - size * 0.35;
      ctx.translate(cx, cy);
      ctx.scale(preset.emphasis.scale, preset.emphasis.scale);
      ctx.translate(-cx, -cy);
    }
    if (emphasised && preset.emphasis?.kind === "box") {
      ctx.save();
      ctx.shadowColor = "transparent";
      ctx.fillStyle = style.accent;
      ctx.beginPath();
      ctx.roundRect(
        x - size * 0.12,
        y - size * 0.82,
        item.width + size * 0.24,
        size * 1.04,
        size * 0.12,
      );
      ctx.fill();
      ctx.restore();
    }
    fillAndStroke(
      ctx,
      text,
      x,
      y,
      size,
      preset.stroke,
      emphasised && preset.emphasis?.kind === "color" ? style.accent : fill,
      trackingPx,
    );
    ctx.restore();
  });
  ctx.restore();
  return bounds;
}
